import { Box, SelectChangeEvent, Theme, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import CustomDropdown from "../../../investorOverview/directEquity/directEquityOverview/CustomDropdown";
import CustomePieChart from "../../../dashboard/chart/CustomePieChart";
import { GetInvestorAssetAllocationResponseBody } from "../../../../redux-store/types/analytics";
import { PortfolioExposureProps } from "./types";
import {
    useDispatch,
    useSelector
} from "react-redux";
import { RootStateType } from "../../../../redux-store/reducers";
import { getInvestorAssetAllocation } from "../../../../redux-store/actions/analytics";
import { ErrorText } from "../../../dashboard/chart/CustomeLineChart";
import { PortfolioExposureLoading } from "../../loadingComponents";
import { NO_DATA_AVAILABLE } from "../../../../utils/constant";

const dropdownOptions = [
    {
        label: "Sector Allocation",
        value: '1'
    },
    {
        label: "Company Exposure",
        value: '2'
    },
]

export default function PortfolioExposure({
    clientCode,
    portfolioId
}: PortfolioExposureProps): JSX.Element {
    const dispatch = useDispatch();

    const [isLoading, setLoading] = useState<boolean>(false)
    const [chartData, setChartData] = useState<GetInvestorAssetAllocationResponseBody>({} as GetInvestorAssetAllocationResponseBody)

    const [selectedOption, setSelectedOption] = useState<string>("1")

    const { iwappRmId = "" } = useSelector((state: RootStateType) => state.id);

    const fetchData = async (clientCode: string, portfolioId: string, category: string) => {
        try {
            setLoading(true)

            const payload = {
                clientCode,
                category,
                rmId: `${iwappRmId}`,
                portfolio: portfolioId
            }

            const response = await (dispatch(getInvestorAssetAllocation(payload)) as unknown as Promise<GetInvestorAssetAllocationResponseBody>);

            setChartData(response)

        } catch (error) {
            console.log((error as Error)?.message);
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        fetchData(clientCode, portfolioId, "Sector")
    }, [clientCode, portfolioId])


    const handleDropdownValueChange = async (value: string | number) => {
        setSelectedOption(`${value}`)
        fetchData(clientCode, portfolioId, value === "1" ? "Sector" : "Issuer")
    }

    if (isLoading) {
        return <PortfolioExposureLoading />
    }

    if (!isLoading && !chartData?.isSuccess) {
        return (
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        paddingBottom: "20px"
                    }}
                >
                    <Typography
                        variant="h2"
                        sx={(theme: Theme) => ({
                            lineHeight: '25px',
                            letterSpacing: "0.01px",
                            color: theme.palette.text.primary,
                        })}
                    >
                        Portfolio Exposure
                    </Typography>
                </Box>
                <Box
                    sx={{
                        flex: 1,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                    }}
                >
                    <Typography
                        variant="body_600"
                        sx={(theme: Theme) => ({
                            fontWeight: 500,
                            lineHeight: "22.5px",
                            letterSpacing: "0.01px",
                            color: theme.palette.text.primary,
                            textAlign: "center",
                            width: "100%"
                        })}
                    >
                        {NO_DATA_AVAILABLE}
                    </Typography>
                </Box>
            </Box>
        )
    }

    return (
        <Box>
            <Box sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center"
            }}>
                <Typography
                    variant="h2"
                    sx={(theme: Theme) => ({
                        lineheight: "25px",
                        letterSpacing: "0.01px",
                        color: theme.palette.text.primary
                    })}
                >
                    Portfolio Exposure
                </Typography>
                <CustomDropdown
                    isLoading={false}
                    selectedValue={selectedOption}
                    handleValueChange={handleDropdownValueChange}
                    dropdownOptions={dropdownOptions}
                />
            </Box>
            <Box sx={{ marginTop: "35px" }}>
                {(
                    (chartData?.result?.data ?? []).length === 0 ||
                    (chartData?.result?.labelData ?? []).length === 0 ||
                    Object.keys(chartData?.result?.totalValue ?? {})?.length === 0
                ) ?
                    <ErrorText /> :
                    <CustomePieChart
                        data={chartData?.result?.data ?? []}
                        isLoading={false}
                        labelData={chartData?.result?.labelData ?? []}
                        totalValue={chartData?.result?.totalValue ?? {}}
                        showSlider
                        align='center'
                        title="Portfolio Exposure"
                    />
                }
            </Box>
        </Box>
    )
}